import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { TREATMENT_CATEGORIES, categoryPath } from "@/data/treatmentCategories";
import logoWide from "@/assets/logo-wide.webp";
import SiteBar from "@/components/SiteBar";

interface NavigationProps {
  onQuoteClick: () => void;
}

/*
 * The menu: HOME, CENTERS, PACKAGES, TREATMENTS, HOLISTIC, KNOWLEDGE,
 * TESTIMONIALS, then the quote button — in that order in the markup, so a
 * keyboard moves through them as they read. Knowledge Center took About Us's
 * place; About Us is in the footer.
 */
const Navigation = ({ onQuoteClick }: NavigationProps) => {
  // the labels in capitals. Capitals run wider, so on desktop they are a size
  // down between lg and xl, where six labels and the button only just fit;
  // the phone menu has room for the larger, spaced form.
  const caps = "uppercase text-[13px] xl:text-sm";
  const capsMobile = "uppercase text-sm tracking-wide";
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [centersDropdownOpen, setCentersDropdownOpen] = useState(false);
  const [programsDropdownOpen, setProgramsDropdownOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [treatmentsDropdownOpen, setTreatmentsDropdownOpen] = useState(false);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);
  const [mobileCentersOpen, setMobileCentersOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [knowledgeDropdownOpen, setKnowledgeDropdownOpen] = useState(false);
  const [mobileKnowledgeOpen, setMobileKnowledgeOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen || isClosing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen, isClosing]);

  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setMobileMenuOpen(false);
    }, 300);
  };

  const centersLinks = [
    { to: "/top-10-ayurvedic-centers-hospitals-bangalore-hyderabad-chennai-south-india", label: "Bangalore & South India" },
    { to: "/top-12-ayurvedic-centers-hospitals-kerala-india", label: "Kerala Region" },
    { to: "/top-15-ayurvedic-centers-hospitals-goa-india", label: "Goa Region" },
    { to: "/top-10-ayurvedic-centers-hospitals-himalayas-rishikesh-uttarakhand-north-east-india", label: "Himalayas & Rishikesh" },
    { to: "/top-11-ayurvedic-centers-hospitals-delhi-ncr-north-india", label: "Delhi, NCR Region" },
    { to: "/top-15-ayurvedic-centers-hospitals-mumbai-pune-nashik-west-india", label: "Mumbai & West India" },
  ];

  const programsLinks = [
    { to: "/ayurveda-packages/panchakarma-detox", label: "Panchakarma Detox" },
    { to: "/ayurveda-packages/disease-specific", label: "Disease-Specific" },
    { to: "/ayurveda-packages/lifestyle-wellness", label: "Lifestyle & Wellness" },
    { to: "/ayurveda-packages/beauty-rejuvenation", label: "Beauty & Rejuvenation" },
    { to: "/ayurveda-packages/integrated-retreat", label: "Integrated Retreat" },
    { to: "/ayurveda-packages/recovery-and-rehabilitation", label: "Recovery & Rehabilitation" },
  ];

  // the six Knowledge tabs the client asked for, shown as plain labels, not
  // links (the client's choice); the Knowledge label itself opens the
  // Knowledge Center
  const knowledgeLinks: { label: string }[] = [
    { label: "Packages" },
    { label: "Treatments" },
    { label: "Destination" },
    { label: "Country" },
    { label: "Wellness" },
    { label: "General" },
  ];
  const knowledgeActive = ["/knowledge-center", "/country-guide", "/ayush-visa"].some((p) =>
    location.pathname.startsWith(p)
  );

  const testimonialsActive = location.pathname.startsWith("/patient-testimonials");

  const servicesLinks = [
    { to: "/holistic-healing/touch-and-bodywork-therapies-in-india", label: "Touch & Bodywork Therapies" },
    { to: "/holistic-healing/energy-and-spiritual-healing-treatments-in-india", label: "Energy & Spiritual Healing" },
    { to: "/holistic-healing/mind-body-interventions-therapies-in-india", label: "Mind-Body Interventions" },
    { to: "/holistic-healing/biological-and-natural-plant-based-therapies-in-india", label: "Biological & Natural/Plant-Based Therapies" },
    { to: "/holistic-healing/specialized-alternative-medical-systems-in-india", label: "Specialized Alternative Medical Systems" },
    { to: "/holistic-healing/ayurveda-ancient-wisdom-for-modern-wellness-in-india", label: "Ayurvedic Healing" },
  ];

  return (
    <>
      <nav className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4">
          {/* Logo left, quote button right, and the menu centred in the space
              between them, the same gap on either side. (Centred on the page
              instead, the wide logo left it crowding the menu on one side and
              a wide gap before the button on the other.) */}
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center space-x-2 shrink-0">
              {/* The wide logo on every page. The artwork has empty space
                  above and below the mark — it fills about 65% of the file's
                  height — so the box is cut to the mark's own proportions
                  (rows 44–244 of 288) and object-cover trims the padding.
                  The same size on a phone and a tablet, where the header
                  holds only the menu button; from xl, beside the full menu,
                  it is larger. */}
              <img
                src={logoWide}
                alt="MyVaidyam — Ayurveda meets medical trust"
                className="aspect-[433/100] h-12 w-auto object-cover xl:h-16"
              />
            </Link>

            {/* Desktop Navigation */}
            {/* the full menu from xl (1280px) up: seven items, the logo and the
                quote button need about 1150px; narrower, the menu button */}
            <div className="hidden xl:flex xl:flex-1 xl:justify-center xl:px-4 items-center gap-6">
              <Link
                to="/"
                className={`font-poppins font-medium transition-colors ${caps} ${
                  location.pathname === "/"
                    ? "text-primary"
                    : "text-foreground hover:text-primary"
                }`}
              >
                Home
              </Link>
              

              {/* Top Centers Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setCentersDropdownOpen(true)}
                onMouseLeave={() => setCentersDropdownOpen(false)}
              >
                <Link
                  to="/top-ayurvedic-centers-in-india"
                  className={`flex items-center gap-1 font-poppins font-medium cursor-pointer transition-colors ${caps} ${
                    location.pathname.startsWith("/top-ayurvedic-centers-in-india") ||
                    location.pathname === "/top-12-ayurvedic-centers-hospitals-kerala-india" ||
                    location.pathname === "/top-15-ayurvedic-centers-hospitals-goa-india" ||
                    location.pathname === "/top-10-ayurvedic-centers-hospitals-himalayas-rishikesh-uttarakhand-north-east-india" ||
                    location.pathname === "/top-11-ayurvedic-centers-hospitals-delhi-ncr-north-india" ||
                    location.pathname === "/mumbai-pune-nashik-west-india-ayurvedic-centers-and-hospitals" ||
                    location.pathname === "/top-15-ayurvedic-centers-hospitals-mumbai-pune-nashik-west-india" ||
                    location.pathname === "/top-10-ayurvedic-centers-hospitals-bangalore-hyderabad-chennai-south-india"
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  Centers
                  <ChevronDown size={16} className={`transition-transform duration-200 ${centersDropdownOpen ? 'rotate-180' : ''}`} />
                </Link>
                
                {/* Dropdown Menu */}
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${centersDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="bg-white border border-border rounded-lg shadow-xl overflow-hidden w-max min-w-[180px]">
                    <Link
                      to="/top-10-ayurvedic-centers-hospitals-bangalore-hyderabad-chennai-south-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Bangalore & South India
                    </Link>
                    <Link
                      to="/top-12-ayurvedic-centers-hospitals-kerala-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Kerala Region
                    </Link>
                    <Link
                      to="/top-15-ayurvedic-centers-hospitals-goa-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Goa Region
                    </Link>
                    <Link
                      to="/top-10-ayurvedic-centers-hospitals-himalayas-rishikesh-uttarakhand-north-east-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Himalayas & Rishikesh
                    </Link>
                    <Link
                      to="/top-11-ayurvedic-centers-hospitals-delhi-ncr-north-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Delhi, NCR Region
                    </Link>
                    <Link
                      to="/top-15-ayurvedic-centers-hospitals-mumbai-pune-nashik-west-india"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setCentersDropdownOpen(false)}
                    >
                      Mumbai & West India
                    </Link>
                  </div>
                </div>
              </div>

              {/* Ayurveda Packages Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setProgramsDropdownOpen(true)}
                onMouseLeave={() => setProgramsDropdownOpen(false)}
              >
                <Link
                  to="/ayurveda-packages"
                  onClick={() => setProgramsDropdownOpen(false)}
                  className={`flex items-center gap-1 font-poppins font-medium cursor-pointer transition-colors ${caps} ${
                    location.pathname.startsWith("/ayurveda-packages")
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  Packages
                  <ChevronDown size={16} className={`transition-transform duration-200 ${programsDropdownOpen ? 'rotate-180' : ''}`} />
                </Link>
                
                {/* Dropdown Menu */}
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${programsDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="bg-white border border-border rounded-lg shadow-xl overflow-hidden w-max min-w-[180px]">
                    <Link
                      to="/ayurveda-packages/panchakarma-detox"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Panchakarma Detox
                    </Link>
                    <Link
                      to="/ayurveda-packages/disease-specific"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Disease-Specific
                    </Link>
                    <Link
                      to="/ayurveda-packages/lifestyle-wellness"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Lifestyle & Wellness
                    </Link>
                    <Link
                      to="/ayurveda-packages/beauty-rejuvenation"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Beauty & Rejuvenation
                    </Link>
                    <Link
                      to="/ayurveda-packages/integrated-retreat"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Integrated Retreat
                    </Link>
                    <Link
                      to="/ayurveda-packages/recovery-and-rehabilitation"
                      className="block whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                      onClick={() => setProgramsDropdownOpen(false)}
                    >
                      Recovery & Rehabilitation
                    </Link>
                  </div>
                </div>
              </div>

              {/* Treatments, with its categories: each opens the page filtered */}
              <div
                className="relative group"
                onMouseEnter={() => setTreatmentsDropdownOpen(true)}
                onMouseLeave={() => setTreatmentsDropdownOpen(false)}
              >
                <Link
                  to="/ayurveda-treatments"
                  onClick={() => setTreatmentsDropdownOpen(false)}
                  className={`flex items-center gap-1 font-poppins font-medium cursor-pointer transition-colors ${caps} ${
                    location.pathname.startsWith("/ayurveda-treatments")
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  Treatments
                  <ChevronDown size={16} className={`transition-transform duration-200 ${treatmentsDropdownOpen ? 'rotate-180' : ''}`} />
                </Link>

                {/* Dropdown Menu: eleven categories, two columns so the list stays short */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${treatmentsDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="grid w-max grid-cols-2 overflow-hidden rounded-lg border border-border bg-white py-2 shadow-xl">
                    {TREATMENT_CATEGORIES.map((category) => (
                      <Link
                        key={category}
                        to={categoryPath(category)}
                        className="block whitespace-nowrap px-5 py-3 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                        onClick={() => setTreatmentsDropdownOpen(false)}
                      >
                        {category}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Services Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <Link
                  to="/holistic-healing"
                  onClick={() => setServicesDropdownOpen(false)}
                  className={`flex items-center gap-1 font-poppins font-medium cursor-pointer transition-colors ${caps} ${
                    location.pathname === "/holistic-healing/ayurveda-ancient-wisdom-for-modern-wellness-in-india" || location.pathname.startsWith("/holistic-healing")
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  Holistic
                  <ChevronDown size={16} className={`transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                </Link>

                {/* Dropdown Menu */}
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${servicesDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  {/* its names are long: a fixed width they wrap within, rather than one wide box */}
                  <div className="bg-white border border-border rounded-lg shadow-xl overflow-hidden w-[280px]">
                    {servicesLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block text-balance px-6 py-4 text-sm font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary"
                        onClick={() => setServicesDropdownOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Knowledge Center, in About Us's place */}
              <div
                className="relative group"
                onMouseEnter={() => setKnowledgeDropdownOpen(true)}
                onMouseLeave={() => setKnowledgeDropdownOpen(false)}
              >
                {/* opens the Knowledge Center page, like the other labels; the
                    guides themselves are in the dropdown on hover */}
                <Link
                  to="/knowledge-center"
                  onClick={() => setKnowledgeDropdownOpen(false)}
                  className={`flex items-center gap-1 font-poppins font-medium cursor-pointer transition-colors ${caps} ${
                    knowledgeActive ? "text-primary" : "text-foreground hover:text-primary"
                  }`}
                >
                  Knowledge
                  <ChevronDown size={16} className={`transition-transform duration-200 ${knowledgeDropdownOpen ? "rotate-180" : ""}`} />
                </Link>
                <div className={`absolute top-full right-0 pt-2 transition-all duration-200 ${knowledgeDropdownOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                  <div className="bg-white border border-border rounded-lg shadow-xl overflow-hidden w-max min-w-[180px]">
                    {knowledgeLinks.map((item) => (
                      <span key={item.label} className="block cursor-default whitespace-nowrap px-6 py-4 text-sm font-medium text-foreground border-l-4 border-transparent">
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/patient-testimonials"
                className={`font-poppins font-medium transition-colors ${caps} ${
                  testimonialsActive ? "text-primary" : "text-foreground hover:text-primary"
                }`}
              >
                Testimonials
              </Link>

            </div>

            <Button onClick={onQuoteClick} className="hidden shrink-0 font-poppins font-semibold xl:inline-flex">
              Get Free Quote
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="xl:hidden text-primary"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => {
                if (mobileMenuOpen) {
                  closeMenu();
                } else {
                  setMobileMenuOpen(true);
                }
              }}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* The bar under the header on every page: breadcrumb, site search and
          language picker. It scrolls away with the page, so it does not take a
          permanent strip of a phone screen the way the sticky header does. */}
      <SiteBar />

      {/* Mobile Menu Overlay */}
      {(mobileMenuOpen || isClosing) && (
        <div className="xl:hidden fixed inset-0 z-40 flex">
          {/* Backdrop with blur */}
          <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
              isClosing ? 'opacity-0' : 'opacity-100 animate-fade-in'
            }`}
            onClick={closeMenu}
          />
          
          {/* Menu Panel */}
          <div
            className={`fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transition-transform duration-300 ease-out ${
              isClosing ? 'translate-x-full' : 'translate-x-0 animate-slide-in-right'
            } flex flex-col`}
          >
            <div className="p-5 border-b border-border shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-primary font-poppins">Menu</h2>
                <button
                  className="text-primary"
                  onClick={closeMenu}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
              <Link
                to="/"
                className={`block py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                  location.pathname === "/"
                    ? "text-primary bg-primary/10"
                    : "text-foreground hover:bg-gray-100"
                }`}
                onClick={closeMenu}
              >
                Home
              </Link>
              

              
              {/* Mobile Centers Section */}
              <div className="space-y-1">
                <Link
                  to="/top-ayurvedic-centers-in-india"
                  className={`w-full flex items-center justify-between py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                    location.pathname.startsWith("/top-ayurvedic-centers-in-india")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-gray-100"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileCentersOpen((prev) => !prev);
                  }}
                >
                  <span>Centers</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileCentersOpen ? "rotate-180" : ""}`} />
                </Link>
                {mobileCentersOpen && (
                  <div className="space-y-1 pl-2">
                    <Link
                      to="/top-ayurvedic-centers-in-india"
                      className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                      onClick={closeMenu}
                    >
                      All Centers
                    </Link>
                    {centersLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Ayurveda Packages Section */}
              <div className="space-y-1">
                <button
                  type="button"
                  className={`w-full text-left flex items-center justify-between py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                    location.pathname.startsWith("/ayurveda-packages")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-gray-100"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileProgramsOpen((prev) => !prev);
                  }}
                >
                  <span>Packages</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileProgramsOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileProgramsOpen && (
                  <div className="space-y-1 pl-2">
                    <Link
                      to="/ayurveda-packages"
                      className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                      onClick={closeMenu}
                    >
                      All Packages
                    </Link>
                    {programsLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* Mobile Treatments Section */}
              <div className="space-y-1">
                <button
                  type="button"
                  className={`w-full text-left flex items-center justify-between py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                    location.pathname.startsWith("/ayurveda-treatments")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-gray-100"
                  }`}
                  onClick={() => setMobileTreatmentsOpen((prev) => !prev)}
                >
                  <span>Treatments</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileTreatmentsOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileTreatmentsOpen && (
                  <div className="space-y-1 pl-2">
                    <Link
                      to="/ayurveda-treatments"
                      className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                      onClick={closeMenu}
                    >
                      All Treatments
                    </Link>
                    {TREATMENT_CATEGORIES.map((category) => (
                      <Link
                        key={category}
                        to={categoryPath(category)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                        onClick={closeMenu}
                      >
                        {category}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Services Section */}
              <div className="space-y-1">
                <button
                  className={`flex items-center justify-between w-full text-left py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                    location.pathname === "/holistic-healing/ayurveda-ancient-wisdom-for-modern-wellness-in-india" || location.pathname.startsWith("/holistic-healing")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-gray-100"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileServicesOpen((prev) => !prev);
                  }}
                >
                  <span>Holistic</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="space-y-1 pl-2">
                    <Link
                      to="/holistic-healing"
                      className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                      onClick={closeMenu}
                    >
                      All Therapies
                    </Link>
                    {servicesLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <button
                  type="button"
                  className={`w-full text-left flex items-center justify-between py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                    knowledgeActive ? "text-primary bg-primary/10" : "text-foreground hover:bg-gray-100"
                  }`}
                  onClick={() => setMobileKnowledgeOpen((prev) => !prev)}
                >
                  <span>Knowledge</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileKnowledgeOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileKnowledgeOpen && (
                  <div className="space-y-1 pl-2">
                      <Link to="/knowledge-center" className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-gray-50 border-l-2 border-primary/20 rounded-md" onClick={closeMenu}>
                        All Guides
                      </Link>
                    {knowledgeLinks.map((item) => (
                      <span key={item.label} className="block cursor-default py-2.5 px-4 text-sm font-medium text-foreground border-l-2 border-primary/20 rounded-md">
                        {item.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/patient-testimonials"
                className={`block py-3 px-4 rounded-lg font-poppins font-medium transition-colors ${capsMobile} ${
                  testimonialsActive ? "text-primary bg-primary/10" : "text-foreground hover:bg-gray-100"
                }`}
                onClick={closeMenu}
              >
                Testimonials
              </Link>
              <Button
                onClick={() => {
                  onQuoteClick();
                  closeMenu();
                }}
                className="w-full font-poppins font-semibold mt-4"
              >
                Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;

